import { Menu, X } from 'lucide-react'
import { useEffect, useState, type MouseEvent } from 'react'
import { useLocation } from 'react-router-dom'

import { useAuth } from '../../../hooks/useAuth'
import { confirmLeaveIfUnsaved } from '../../../hooks/useUnsavedWork'
import { ROUTES } from '../../../routes/types'
import { Avatar } from '../../atoms'
import { Logo } from '../../atoms/Logo'
import { CreditBalanceIndicator } from '../../molecules/CreditBalanceIndicator'
import { NavLink } from '../../molecules/NavLink'
import { RobotAlertsBell } from '../../molecules/RobotAlertsBell'
import { SettingsDrawer } from '../../molecules/SettingsDrawer'
import { usePlanningApplicant } from './usePlanningApplicant'

interface NavItem {
  label: string
  to: string
  exact?: boolean
  requiredLicense?: string[]
  requiredRole?: Array<'admin' | 'publisher' | 'viewer'>
}

const navItems: NavItem[] = [
  { label: 'Home', to: ROUTES.HOME, exact: true },
  { label: 'Map', to: ROUTES.MAP },
  { label: 'Library', to: ROUTES.LIBRARY },
  { label: 'Dashboards', to: ROUTES.DASHBOARD },
  { label: 'Forms', to: ROUTES.FORMS, requiredLicense: ['forms'], requiredRole: ['admin', 'publisher'] },
  {
    label: 'Geocoding',
    to: ROUTES.GEOCODING,
    requiredLicense: ['geocoding'],
    requiredRole: ['admin', 'publisher'],
  },
  { label: 'Scripts', to: ROUTES.SCRIPTS, requiredLicense: ['scripts'], requiredRole: ['admin', 'publisher'] },
  { label: 'Security', to: ROUTES.SECURITY, requiredLicense: ['security'], requiredRole: ['admin'] },
  // `monitoring` IS a real entitlement module: ModuleLabel.MONITORING exists,
  // the license server issues it (services/license/admin AVAILABLE_MODULES),
  // and the backend gates it with requireModule. It — and `geocoding` — were
  // the only two licensable nav entries carrying no requiredLicense, so an
  // unlicensed install advertised both in the header while every API call
  // behind them 403'd. No requiredRole on Monitoring: a viewer sees the page
  // and simply doesn't see the identity-gated panels (canViewIdentities from
  // GET /monitoring/sites).
  { label: 'Monitoring', to: ROUTES.MONITORING, requiredLicense: ['monitoring'] },
  // Planning (Kebenaran Merancang). No requiredRole, on the Monitoring precedent:
  // read-only officers and applicants are viewers, and the backend's
  // requirePlanningActor decides what each caller may do (spec section 5).
  { label: 'Planning', to: ROUTES.PLANNING, requiredLicense: ['planning'] },
  { label: 'Settings', to: ROUTES.SETTINGS, requiredRole: ['admin'] },
]

// Planning applicants are guest viewers that planning never shares a layer, map or dashboard with
// (spec section 7.1; plan F3), so these pages would be empty or show only public items. Hiding a
// link is not access control: the routes behave as before.
const APPLICANT_HIDDEN: ReadonlySet<string> = new Set([ROUTES.MAP, ROUTES.LIBRARY, ROUTES.DASHBOARD])

interface AppHeaderProps {
  logoUrl?: string | null
}

export function AppHeader({ logoUrl }: AppHeaderProps) {
  const { user } = useAuth()
  const planningApplicant = usePlanningApplicant(user)
  const location = useLocation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isNavOpen, setIsNavOpen] = useState(false)

  // Close the mobile menu on any navigation (link click, back button, redirect).
  useEffect(() => {
    setIsNavOpen(false)
  }, [location.pathname])

  const visibleNavItems = navItems.filter(item => {
    if (planningApplicant && APPLICANT_HIDDEN.has(item.to)) return false

    if (item.requiredLicense?.length) {
      const hasLicense = item.requiredLicense.some(lic => user?.licenses.includes(lic))
      if (!hasLicense) return false
    }

    if (item.requiredRole?.length) {
      if (!user?.role || !item.requiredRole.includes(user.role)) return false
    }

    return true
  })

  // Guard the human navigation path the same way the assistant's `navigate_to`
  // tool is guarded: a confirm prompt describing what would be lost, cancel
  // to stay put. Does not cover a bare `navigate()` call elsewhere in the app
  // (see `useUnsavedWork.ts`) — this only intercepts clicks on these NavLinks.
  const guardNavClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!confirmLeaveIfUnsaved()) event.preventDefault()
  }

  return (
    <>
      <header
        className="relative flex h-[65px] shrink-0 items-center px-4"
        style={{ backgroundColor: 'var(--brand-primary-dark)' }}
      >
        {/* Left: Logo */}
        <Logo customLogoUrl={logoUrl} />

        {/* Center: Navigation (inline ≥ md; collapses to the hamburger below md) */}
        <nav className="mx-6 hidden flex-1 items-center gap-1 md:flex">
          {visibleNavItems.map(item => (
            <NavLink key={item.to} to={item.to} label={item.label} exact={item.exact} onClick={guardNavClick} />
          ))}
        </nav>

        {/* Right: Credit balance + alerts bell + hamburger (mobile) + User */}
        <div className="ml-auto flex items-center gap-3">
          <CreditBalanceIndicator />
          <RobotAlertsBell />
          <button
            type="button"
            onClick={() => setIsNavOpen(open => !open)}
            aria-label={isNavOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isNavOpen}
            aria-controls="app-mobile-nav"
            className="flex items-center rounded-lg p-2 text-white/80 transition-colors hover:bg-white/10 md:hidden"
          >
            {isNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="flex items-center rounded-lg p-1 transition-colors hover:bg-white/10"
          >
            <Avatar name={user?.name} size="md" />
          </button>
        </div>

        {/* Mobile navigation drawer (below md only) */}
        {isNavOpen && (
          <>
            <button
              type="button"
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => setIsNavOpen(false)}
              className="fixed inset-x-0 bottom-0 top-[65px] z-30 cursor-default bg-black/30 md:hidden"
            />
            <nav
              id="app-mobile-nav"
              className="absolute inset-x-0 top-[65px] z-40 flex flex-col gap-1 border-t border-white/10 p-2 shadow-lg md:hidden"
              style={{ backgroundColor: 'var(--brand-primary-dark)' }}
            >
              {visibleNavItems.map(item => (
                <NavLink key={item.to} to={item.to} label={item.label} exact={item.exact} onClick={guardNavClick} />
              ))}
            </nav>
          </>
        )}
      </header>

      <SettingsDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </>
  )
}
